import { useState, useEffect, useCallback, useRef } from 'react';
import { flows } from '../services/api';
import { flowCompletionStatusUrl, FlowCompletionContext } from '../services/api/flowsClient';
import { FlowCompletionStatus } from '../types';

interface CachedStatus {
  status: FlowCompletionStatus | null;
  timestamp: number;
}

const CACHE_DURATION = 30000; // Cache for 30 seconds
const statusCache: { [key: string]: CachedStatus } = {};
const buildCacheKey = (flowName: string, suffix?: string, context: FlowCompletionContext = 'runtime') =>
  JSON.stringify([flowCompletionStatusUrl(flowName, context), context, suffix ?? 'global']);

export const setCachedFlowCompletionStatus = (
  flowName: string,
  status: FlowCompletionStatus | null,
  cacheKeySuffix?: string,
  context: FlowCompletionContext = 'runtime',
) => {
  statusCache[buildCacheKey(flowName, cacheKeySuffix, context)] = {
    status,
    timestamp: Date.now(),
  };
};

export const clearCachedFlowCompletionStatus = (
  flowName: string,
  cacheKeySuffix?: string,
  context: FlowCompletionContext = 'runtime',
) => {
  delete statusCache[buildCacheKey(flowName, cacheKeySuffix, context)];
};

interface UseCachedFlowCompletionStatusResult {
  completionStatus: FlowCompletionStatus | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to check if a flow has been completed with caching support
 * @param flowName The name of the flow to check
 * @param fallbackToUserData If true, will use user.has_completed_intake as fallback when API fails
 * @returns Object containing completionStatus, loading state, error state, and refetch function
 */
export const useCachedFlowCompletionStatus = (
  flowName: string,
  fallbackToUserData: boolean = false,
  enabled: boolean = true,
  cacheKeySuffix?: string,
  context: FlowCompletionContext = 'runtime',
): UseCachedFlowCompletionStatusResult => {
  const [completionStatus, setCompletionStatus] = useState<FlowCompletionStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const requestVersion = useRef(0);

  const cacheKey = buildCacheKey(flowName, cacheKeySuffix, context);

  const fetchCompletionStatus = useCallback(async (force: boolean = false) => {
    if (!enabled) {
      return;
    }
    const requestId = ++requestVersion.current;

    // Check cache first
    const cached = statusCache[cacheKey];
    const now = Date.now();

    if (!force && cached && (now - cached.timestamp) < CACHE_DURATION) {
      setCompletionStatus(cached.status);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const status = await flows.getCompletionStatus(flowName, context);
      if (requestId !== requestVersion.current) return;
      // Update cache
      statusCache[cacheKey] = { status, timestamp: Date.now() };

      setCompletionStatus(status);
      setError(null);
    } catch (err) {
      if (requestId !== requestVersion.current) return;
      console.error('Error fetching flow completion status:', err);
      setError('Failed to fetch flow completion status');
      
      // If API call fails and we're using fallback, create a fallback status
      if (fallbackToUserData) {
        const userData = localStorage.getItem('user');
        if (userData) {
          try {
            const user = JSON.parse(userData);
            if (user && typeof user.has_completed_intake === 'boolean') {
              const fallbackStatus: FlowCompletionStatus = {
                completed_at: user.has_completed_intake ? new Date().toISOString() : null,
                flow_name: flowName
              };
              // Update cache with fallback
              statusCache[cacheKey] = { status: fallbackStatus, timestamp: Date.now() };

              setCompletionStatus(fallbackStatus);
            }
          } catch (e) {
            console.error('Error parsing user data for fallback:', e);
          }
        }
      }
    } finally {
      if (requestId === requestVersion.current) setLoading(false);
    }
  }, [cacheKey, context, enabled, fallbackToUserData, flowName]);

  useEffect(() => {
    setCompletionStatus(null);
    setError(null);
    if (!enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchCompletionStatus();
    return () => { requestVersion.current += 1; };
  }, [enabled, fetchCompletionStatus, cacheKey]);

  const refetch = useCallback(async () => {
    if (!enabled) {
      return;
    }

    await fetchCompletionStatus(true);
  }, [enabled, fetchCompletionStatus]);

  return {
    completionStatus,
    loading,
    error,
    refetch
  };
};

export default useCachedFlowCompletionStatus;
